// WellsTab.js
// This is the Wells Tab for the Sampling Event Form

import React, { useRef, useEffect, useMemo } from "react";
// Shared Components
import { FilterMenu } from "../../common/WellSelectionFilterMenu";
import { WellCard } from "./components/WellCard";
import { GroupCard } from "./components/GroupCard";
// CSS Styles
import styles from "./WellsTab.module.css";

// Main WellsTab Component
export const WellsTab = ({
  samplingEventData,
  tabProps,
  actions,
  samplingEventMode,
}) => {
  // Getting the actions and ui from the tab props
  const wellsActions = actions?.samplingEventFormActions?.wells;
  const ui = tabProps?.wellsUI || {};
  const user = tabProps?.user;

  // Get all employees from the sampling event data for the dropdown
  const allEmployees = samplingEventData?.samplingGroups
    ? samplingEventData.samplingGroups.flatMap((group) =>
        group.employees.map((employee) => ({
          employee_id: employee.employeeId,
          name: employee.employeeName,
        }))
      )
    : [];

  // This will only include the Team Lead and the Event Creator
  const qaQcEmployees = [
    {
      employee_id: samplingEventData.teamLead,
      name: samplingEventData.teamLeadName,
    },
    {
      employee_id: samplingEventData.createdBy,
      name: samplingEventData.createdByName,
    },
  ];

  // Emit patch function, to be passed to each well card to facilitate the sending of information to the server via websocket.
  const emitPatch = tabProps?.emitPatch;

  const wells = useMemo(() => {
    return samplingEventData?.samplingEventWells || [];
  }, [samplingEventData]);

  const groupWellIds = useMemo(() => {
    const groups = samplingEventData?.samplingGroups || [];
    return groups.flatMap((g) => g.wells.map((w) => w.wellId));
  }, [samplingEventData]);

  const myGroupWellIds = useMemo(() => {
    const groups = samplingEventData?.samplingGroups || [];
    const myEmpId = tabProps?.user?.employee_id;
    const myGroups = groups.filter((g) =>
      g.employees.some((e) => e.employeeId === myEmpId)
    );
    return myGroups.flatMap((g) => g.wells.map((w) => w.wellId));
  }, [samplingEventData, tabProps?.user]);

  // Getting the expanded well id from the ui state
  const expandedWellId = ui.expandedWellId;
  const handleExpandWell = (wellId) => wellsActions.setExpandedWell(wellId);

  // State to track which group card is expanded
  const expandedGroupId = ui.expandedGroupId;
  const handleExpandGroup = (groupId) =>
    wellsActions.setExpandedGroupId(groupId);

  // Access nested filter state - ui.filters contains { isFilterMenuOpen, filters: {...}, sort: {...} }
  const filterState = ui.filters || {};
  const activeFilters = filterState.filters || {};
  const activeSort = filterState.sort || { field: "wellId", direction: "asc" };

  // Alphanumeric (natural) sort comparator for well IDs
  // Properly sorts "MW-1", "MW-2", "MW-10" instead of "MW-1", "MW-10", "MW-2"
  const alphanumericCompare = (a, b) => {
    const chunkify = (str) => {
      const chunks = [];
      let currentChunk = "";
      let isDigit = false;

      for (const char of str) {
        const charIsDigit = /\d/.test(char);
        if (currentChunk && charIsDigit !== isDigit) {
          chunks.push(isDigit ? parseInt(currentChunk, 10) : currentChunk);
          currentChunk = "";
        }
        currentChunk += char;
        isDigit = charIsDigit;
      }
      if (currentChunk) {
        chunks.push(isDigit ? parseInt(currentChunk, 10) : currentChunk);
      }
      return chunks;
    };

    const chunksA = chunkify(String(a).toLowerCase());
    const chunksB = chunkify(String(b).toLowerCase());

    for (let i = 0; i < Math.max(chunksA.length, chunksB.length); i++) {
      const chunkA = chunksA[i];
      const chunkB = chunksB[i];

      if (chunkA === undefined) return -1;
      if (chunkB === undefined) return 1;

      if (typeof chunkA === "number" && typeof chunkB === "number") {
        if (chunkA !== chunkB) return chunkA - chunkB;
      } else {
        const strA = String(chunkA);
        const strB = String(chunkB);
        if (strA !== strB) return strA.localeCompare(strB);
      }
    }
    return 0;
  };

  const filteredAndSortedWells = useMemo(() => {
    if (!wells || wells.length === 0) return [];

    let result = [...wells];

    // Filter by well ID text search
    if (activeFilters.wellId) {
      const q = String(activeFilters.wellId).toLowerCase();
      result = result.filter((w) => (w.wellId || "").toLowerCase().includes(q));
    }

    // Filter by well status (exact match for dropdown)
    if (activeFilters.status) {
      result = result.filter((w) => w.status === activeFilters.status);
    }

    // Filter by sampling status (completed/incomplete)
    if (activeFilters.samplingStatus === "completed") {
      result = result.filter((w) => w.samplingCompleted === true);
    } else if (activeFilters.samplingStatus === "incomplete") {
      result = result.filter((w) => w.samplingCompleted !== true);
    }

    // Filter by group assignment
    if (activeFilters.groupAssignment === "assigned") {
      result = result.filter((w) => groupWellIds.includes(w.wellId));
    } else if (activeFilters.groupAssignment === "unassigned") {
      result = result.filter((w) => !groupWellIds.includes(w.wellId));
    }

    // Tab-based filtering (separate from filter menu)
    if (ui.activeTab === "groupWells") {
      result = result.filter((w) => groupWellIds.includes(w.wellId));
    } else if (ui.activeTab === "myGroupWells") {
      result = result.filter((w) => myGroupWellIds.includes(w.wellId));
    }

    // Sort results
    const field = activeSort.field || "wellId";
    const dir = activeSort.direction === "desc" ? -1 : 1;

    result.sort((a, b) => {
      const av = a[field] ?? "";
      const bv = b[field] ?? "";

      // Use alphanumeric sort for wellId, standard localeCompare for other fields
      if (field === "wellId") {
        return alphanumericCompare(av, bv) * dir;
      }

      const as = typeof av === "string" ? av : String(av);
      const bs = typeof bv === "string" ? bv : String(bv);
      return as.localeCompare(bs) * dir;
    });

    return result;
  }, [
    wells,
    activeFilters,
    activeSort,
    ui.activeTab,
    groupWellIds,
    myGroupWellIds,
  ]);

  // Use nested filter state for FilterMenu component
  const wellFilters = activeFilters;
  const setWellFilters = (filters) => wellsActions.applyFilters(filters);
  const wellSort = activeSort;
  const setWellSort = (sort) => wellsActions.setSort(sort);
  const isFilterMenuOpen = !!filterState.isFilterMenuOpen;
  const setIsFilterMenuOpen = (open) => {
    if (open) {
      wellsActions.openFilterMenu();
    } else {
      wellsActions.closeFilterMenu();
    }
  };

  // Create refs for filter button and menu
  const filterButtonRef = useRef(null);
  const filterMenuRef = useRef(null);

  // Handle clicks outside filter menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isFilterMenuOpen) return;

      if (
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target) &&
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target)
      ) {
        setIsFilterMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterMenuOpen]);

  // Toggle filter menu
  const toggleFilterMenu = () => wellsActions.toggleFilterMenu();

  // Apply filters (called from FilterMenu component)
  const applyFilters = (filters, sort) => {
    setWellFilters(filters);
    setWellSort(sort);
  };

  // Reset filters (called from FilterMenu component)
  const resetFilters = () => {
    wellsActions.resetAll();
  };

  // Display message if no wells available in the sampling event
  if (!wells || wells.length === 0) {
    return (
      <div className={styles.wellsTabContainer}>
        <div className={styles.noWells}>
          No wells available for sampling. Please make sure wells are assigned
          to this sampling event.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wellsTabContainer}>
      {/* Tab buttons */}
      <div className={styles.tabsContainer}>
        <button
          className={`${styles.tabButton} ${
            ui.activeTab === "myGroupWells" ? styles.tabButtonActive : ""
          }`}
          onClick={() => wellsActions.setActiveTab("myGroupWells")}
        >
          My Group Wells
        </button>
        <button
          className={`${styles.tabButton} ${
            ui.activeTab === "groupWells" ? styles.tabButtonActive : ""
          }`}
          onClick={() => wellsActions.setActiveTab("groupWells")}
        >
          Group Wells
        </button>

        <button
          className={`${styles.tabButton} ${
            ui.activeTab === "allWells" ? styles.tabButtonActive : ""
          }`}
          onClick={() => wellsActions.setActiveTab("allWells")}
        >
          All Wells
        </button>
      </div>

      {/* Filter Controls */}
      <div className={styles.filterControls}>
        <div className={styles.filterButtonContainer}>
          <button
            className={styles.filterButton}
            onClick={toggleFilterMenu}
            ref={filterButtonRef}
          >
            Filter & Sort Wells
          </button>
          {isFilterMenuOpen && (
            <div ref={filterMenuRef}>
              <FilterMenu
                wellFilters={wellFilters}
                setWellFilters={setWellFilters}
                wellSort={wellSort}
                setWellSort={setWellSort}
                applyFilters={applyFilters}
                resetFilters={resetFilters}
                closeFilterMenu={() => setIsFilterMenuOpen(false)}
                styles={styles}
                filterType="sampling"
                wells={wells}
                showGroupFilter={true}
              />
            </div>
          )}
        </div>
      </div>

      {/* Well Cards Section */}
      <div className={styles.wellsCardsContainer}>
        {ui.activeTab === "groupWells" && samplingEventData.samplingGroups ? (
          // Group Wells Tab Content
          samplingEventData.samplingGroups.length > 0 ? (
            <div className={styles.groupCardsContainer}>
              {samplingEventData.samplingGroups.map((group) => (
                <GroupCard
                  key={group.id}
                  group={group}
                  isExpanded={expandedGroupId === group.id}
                  onToggle={() => handleExpandGroup(group.id)}
                  allWells={wells}
                  expandedCardId={expandedWellId}
                  handleCardToggle={handleExpandWell}
                  allEmployees={allEmployees}
                  qaQcEmployees={qaQcEmployees}
                  samplingEventMode={samplingEventMode}
                  actions={wellsActions}
                  ui={ui}
                  emitPatch={emitPatch}
                />
              ))}
            </div>
          ) : (
            <div className={styles.noWells}>
              <p>No sampling groups available in this event.</p>
            </div>
          )
        ) : // My Group Wells or All Wells Tabs Content
        filteredAndSortedWells.length > 0 ? (
          <>
            <p>Click on a well card to expand and view/edit sampling details</p>

            {filteredAndSortedWells.map((well) => (
              <WellCard
                key={well.id}
                well={well}
                isExpanded={expandedWellId === well.id}
                onToggle={() => handleExpandWell(well.id)}
                allEmployees={allEmployees}
                qaQcEmployees={qaQcEmployees}
                samplingEventMode={samplingEventMode}
                actions={wellsActions}
                ui={ui}
                emitPatch={emitPatch}
              />
            ))}
          </>
        ) : (
          <div className={styles.noWells}>
            <p>No wells match the current filters.</p>
            <button className={styles.resetButton} onClick={resetFilters}>
              Reset All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
